import { Field, ErrorMessage } from 'formik';
import { FieldEntityProps, SmsCheckBoxClassNamesInterface } from '../types';
import { getSmsOptInValues } from '../utils';

/**
 * AccessibleSmsCheckbox - WCAG AA compliant SMS opt-in checkbox
 *
 * This component intentionally does NOT use <fieldset>/<legend> wrapper.
 *
 * WAVE accessibility scanner may show a "Missing fieldset" ALERT (yellow).
 * This is a suggestion, NOT an error or WCAG violation.
 *
 * Per WebAIM (the creators of WAVE):
 * "Fieldset and legend should only be used when a higher-level label is necessary.
 * Single checkboxes or basic radio buttons that make sense from their labels alone
 * do not require fieldset and legend."
 *
 * @see https://webaim.org/techniques/forms/controls
 *
 * The SMS opt-in checkbox:
 * - Is a SINGLE checkbox (not a group of related checkboxes)
 * - Has its own descriptive <label> with htmlFor association
 * - Does NOT require fieldset/legend per WebAIM guidelines
 *
 * Using fieldset/legend for a single checkbox caused WAVE to report
 * "Multiple form labels" ERROR because both the <legend> and <label>
 * were being counted as label sources for the same input.
 *
 * @see https://webaim.org/techniques/forms/advanced (form labeling)
 */
interface AccessibleSmsCheckboxProps {
  field: FieldEntityProps | undefined;
  smsCheckBoxFieldClassNames: SmsCheckBoxClassNamesInterface;
  isTouchedAndError: boolean;
  showErrorMessages?: boolean;
  idSuffix?: string;
}

const AccessibleSmsCheckbox = ({
  field,
  smsCheckBoxFieldClassNames,
  isTouchedAndError,
  showErrorMessages = false,
  idSuffix = '',
}: AccessibleSmsCheckboxProps) => {
  const name = field?.parameterName || 'sms_opt_in';
  const inputId = idSuffix ? `${name}${idSuffix}` : name;
  const { smsText, smsCtaText, smsCtaHref } = getSmsOptInValues(
    field ? [field] : [],
  );
  const errorId = `${inputId}-error`;

  return (
    <div className={smsCheckBoxFieldClassNames.container}>
      <Field
        type="checkbox"
        name={name}
        id={inputId}
        className={smsCheckBoxFieldClassNames.mainField}
        aria-describedby={
          showErrorMessages && isTouchedAndError ? errorId : undefined
        }
        aria-invalid={isTouchedAndError}
      />
      <div className={smsCheckBoxFieldClassNames.textAndErrorContainer}>
        <label
          htmlFor={inputId}
          className={smsCheckBoxFieldClassNames.disclaimerTextClassName}
        >
          {smsText?.value}{' '}
          <a href={smsCtaHref} target="_blank" rel="noopener noreferrer">
            {smsCtaText?.value}
          </a>
        </label>
        {showErrorMessages && isTouchedAndError && (
          <div
            id={errorId}
            role="alert"
            className={`${smsCheckBoxFieldClassNames.error} smsCheckBoxErrorMessage`}
          >
            <ErrorMessage name={name} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessibleSmsCheckbox;
